/* ----------------------------------------------------------------------------
 * Sales-B (W2b) data sources — ONE mock/real gate per surface, self-contained
 * and owned end-to-end by Agent SALES-B-FE (kept out of the shared
 * `data/sources.ts` so W2b/W2a stay file-disjoint).
 *
 * Each fetcher is, exactly like the rest of the app:
 *
 *     USE_MOCK_DATA ? mock : real fetch via the api client
 *
 * Real mode targets the LOCKED W2b crm contract (CONTRACTS.md §"Locked API
 * contract"): three GET, all READ-ONLY over EXISTING event/opportunity data —
 * no send, no charge, workspace-scoped server-side. Adapters are honest by
 * construction: a value the backend cannot attribute stays null (numOrNull
 * never fabricates a zero) — revenue is null until an opportunity is Won.
 *
 * Mocks are STRICTLY behind USE_MOCK_DATA — never served as real data.
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA } from '@/lib/api';
import type {
  OutreachEvent,
  OutreachEventType,
  OutreachTracking,
  PipelineSummary,
  PipelineSummaryStage,
  RevenueAttribution,
  RevenueAttributionRow,
} from '@/types/salesb';

/* ============================================================================
 * Null-honest coercion — absent / blank / non-numeric all stay null (never a
 * fabricated zero). Distinct from `Number(x) || 0`, which is only correct where
 * 0 is a real, counted metric (funnel counts, lead counts).
 * ========================================================================== */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Real, counted integer — 0 is a genuine value here (a stage with no events). */
function intOf(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function strOf(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v !== '' ? v : fallback;
}

/* ============================================================================
 * API DTOs — the real response shapes (locked W2b crm envelopes).
 * ========================================================================== */
interface OutreachEventDto {
  lead_id: string | number;
  business_name?: string | null;
  event_type?: string | null;
  campaign?: string | null;
  at?: string | null;
  detail?: string | null;
}

interface OutreachTrackingDto {
  events?: OutreachEventDto[];
  funnel?: { sent?: number | string; opened?: number | string; clicked?: number | string; replied?: number | string };
  source?: string;
}

interface PipelineSummaryStageDto {
  stage?: string | null;
  count?: number | string | null;
  value?: number | string | null;
  currency?: string | null;
}

interface PipelineSummaryDto {
  stages?: PipelineSummaryStageDto[];
  total_value?: number | string | null;
  source?: string;
}

interface RevenueAttributionRowDto {
  source_channel?: string | null;
  leads?: number | string | null;
  replies?: number | string | null;
  opportunities?: number | string | null;
  won_value?: number | string | null;
  currency?: string | null;
  revenue_per_lead?: number | string | null;
}

interface RevenueAttributionDto {
  attribution?: RevenueAttributionRowDto[];
  note?: string;
  source?: string;
}

/* ============================================================================
 * Adapters — real API shapes → the UI types. Null-honest by construction.
 * ========================================================================== */
const VALID_EVENT_TYPES: OutreachEventType[] = ['SENT', 'OPENED', 'CLICKED', 'REPLIED', 'BOUNCED'];

function adaptEventType(raw: unknown): OutreachEventType {
  const up = typeof raw === 'string' ? raw.toUpperCase() : '';
  return (VALID_EVENT_TYPES as string[]).includes(up) ? (up as OutreachEventType) : 'SENT';
}

function adaptOutreachEvent(dto: OutreachEventDto): OutreachEvent {
  return {
    lead_id: String(dto.lead_id ?? ''),
    business_name: strOf(dto.business_name, 'Unknown business'),
    event_type: adaptEventType(dto.event_type),
    campaign: strOf(dto.campaign),
    at: strOf(dto.at),
    detail: strOf(dto.detail),
  };
}

function adaptPipelineStage(dto: PipelineSummaryStageDto): PipelineSummaryStage {
  return {
    stage: strOf(dto.stage, '—'),
    count: intOf(dto.count),
    value: numOrNull(dto.value) ?? 0,
    currency: strOf(dto.currency, 'GBP'),
  };
}

function adaptAttributionRow(dto: RevenueAttributionRowDto): RevenueAttributionRow {
  return {
    source_channel: strOf(dto.source_channel, '(unattributed)'),
    leads: intOf(dto.leads),
    replies: intOf(dto.replies),
    opportunities: intOf(dto.opportunities),
    // Null-honest: won_value stays null until a booking/opportunity is Won.
    won_value: numOrNull(dto.won_value),
    currency: strOf(dto.currency, 'GBP'),
    revenue_per_lead: numOrNull(dto.revenue_per_lead),
  };
}

/* ============================================================================
 * MOCKS — dev-only, STRICTLY behind USE_MOCK_DATA. Never served as real data.
 * Deliberately include honest-null / honest-zero cases so the empty states are
 * exercised in mock mode too.
 * ========================================================================== */
const MOCK_OUTREACH_TRACKING: OutreachTracking = {
  events: [
    { lead_id: 'lead_1042', business_name: 'Cardiff Pilates Studio', event_type: 'REPLIED', campaign: 'wales-pilates-q3', at: '2026-07-16T14:20:00Z', detail: 'Positive reply — asked about migration from Fresha' },
    { lead_id: 'lead_1042', business_name: 'Cardiff Pilates Studio', event_type: 'CLICKED', campaign: 'wales-pilates-q3', at: '2026-07-16T09:12:00Z', detail: 'Clicked pricing link' },
    { lead_id: 'lead_1039', business_name: 'Riverside Yoga', event_type: 'OPENED', campaign: 'wales-pilates-q3', at: '2026-07-16T08:40:00Z', detail: 'Opened intro email' },
    { lead_id: 'lead_1051', business_name: 'The Movement Rooms', event_type: 'SENT', campaign: 'bristol-studios-q3', at: '2026-07-16T06:00:00Z', detail: 'Intro email · template intro_v3' },
    { lead_id: 'lead_1033', business_name: 'Bath Barre Co', event_type: 'BOUNCED', campaign: 'bristol-studios-q3', at: '2026-07-15T18:05:00Z', detail: 'Hard bounce — mailbox does not exist' },
    { lead_id: 'lead_1028', business_name: 'Newport Sports Therapy', event_type: 'OPENED', campaign: 'wales-pilates-q3', at: '2026-07-15T16:30:00Z', detail: 'Opened follow-up' },
    { lead_id: 'lead_1028', business_name: 'Newport Sports Therapy', event_type: 'SENT', campaign: 'wales-pilates-q3', at: '2026-07-15T06:00:00Z', detail: 'Follow-up 1 · template followup_a' },
  ],
  funnel: { sent: 214, opened: 132, clicked: 41, replied: 12 },
  source: 'bksesh_sales_lead_events',
};

const MOCK_PIPELINE_SUMMARY: PipelineSummary = {
  stages: [
    { stage: 'New', count: 18, value: 9200, currency: 'GBP' },
    { stage: 'Contacted', count: 11, value: 6400, currency: 'GBP' },
    { stage: 'Qualified', count: 6, value: 4100, currency: 'GBP' },
    { stage: 'Proposal', count: 3, value: 2850, currency: 'GBP' },
    { stage: 'Won', count: 2, value: 1980, currency: 'GBP' },
    { stage: 'Lost', count: 4, value: 0, currency: 'GBP' },
  ],
  total_value: 24530,
  currency: 'GBP',
  source: 'bc_crm_opportunities',
};

const MOCK_REVENUE_ATTRIBUTION: RevenueAttribution = {
  attribution: [
    { source_channel: 'wales-pilates-q3', leads: 94, replies: 9, opportunities: 5, won_value: 1980, currency: 'GBP', revenue_per_lead: 21.06 },
    // Honest-null case: real activity, but nothing Won yet → revenue stays null.
    { source_channel: 'bristol-studios-q3', leads: 61, replies: 3, opportunities: 1, won_value: null, currency: 'GBP', revenue_per_lead: null },
    { source_channel: 'directory-import', leads: 40, replies: 0, opportunities: 0, won_value: null, currency: 'GBP', revenue_per_lead: null },
  ],
  note: 'attribution joins sales events → opportunities; revenue null until a booking/opportunity is Won',
  source: 'bksesh_sales_lead_events+bc_crm_opportunities',
  window: 90,
};

/* ============================================================================
 * Surface sources — one gated fetcher per screen. All FREE, read-only.
 * ========================================================================== */

// ---- Outreach Tracking (/sales/outreach) -----------------------------------
export async function fetchOutreachTracking(limit = 100, projectId?: number): Promise<OutreachTracking> {
  if (USE_MOCK_DATA) return MOCK_OUTREACH_TRACKING;
  // Domain scope: pass the Viewed domain's project_id so only its outreach
  // events/funnel return (host-matched via the originating lead). Absent =>
  // workspace-wide. Never another domain's events.
  const res = await api.get<OutreachTrackingDto>('/crm/outreach-tracking', { limit, ...(projectId ? { project_id: projectId } : {}) });
  return {
    events: (res.events ?? []).map(adaptOutreachEvent),
    funnel: {
      sent: intOf(res.funnel?.sent),
      opened: intOf(res.funnel?.opened),
      clicked: intOf(res.funnel?.clicked),
      replied: intOf(res.funnel?.replied),
    },
    source: strOf(res.source, 'bksesh_sales_lead_events'),
  };
}

// ---- Pipeline Summary (surfaced on /sales/pipeline) ------------------------
export async function fetchPipelineSummary(projectId?: number): Promise<PipelineSummary> {
  if (USE_MOCK_DATA) return MOCK_PIPELINE_SUMMARY;
  // Domain scope: opportunities are matched to the Viewed domain via their
  // originating lead's website host. Absent => workspace-wide.
  const res = await api.get<PipelineSummaryDto>('/crm/pipeline-summary', projectId ? { project_id: projectId } : undefined);
  const stages = (res.stages ?? []).map(adaptPipelineStage);
  // Dominant currency across the stages, for the total line (default GBP).
  const currency = stages.find((s) => s.value > 0)?.currency ?? stages[0]?.currency ?? 'GBP';
  return {
    stages,
    total_value: numOrNull(res.total_value) ?? 0,
    currency,
    source: strOf(res.source, 'bc_crm_opportunities'),
  };
}

// ---- Revenue Attribution (/sales/revenue-attribution) ----------------------
export async function fetchRevenueAttribution(window = 90): Promise<RevenueAttribution> {
  if (USE_MOCK_DATA) return { ...MOCK_REVENUE_ATTRIBUTION, window };
  const res = await api.get<RevenueAttributionDto>('/crm/revenue-attribution', { window });
  return {
    attribution: (res.attribution ?? []).map(adaptAttributionRow),
    note:
      strOf(res.note) ||
      'attribution joins sales events → opportunities; revenue null until a booking/opportunity is Won',
    source: strOf(res.source, 'bksesh_sales_lead_events+bc_crm_opportunities'),
    window,
  };
}
